import { useEffect, useRef, useState } from 'react'
import { useForm } from 'react-final-form'
import cx from 'classnames'

import ConfirmationModal from 'common/components/confirmationModal/ConfirmationModal'
import MenuButton, { MenuItem } from 'common/components/menuButton'
import {
  Add,
  Clipboard,
  CloseSmall,
  Email,
  File as FileIcon,
  ImageIcon,
} from 'common/icons'

import ImportContentModal, { ImportTab } from './ImportContentModal'

import menuButtonStyles from 'common/components/menuButton/MenuButton.module.css'

type GenerateBriefFromContentOptions = {
  brandVoiceId?: string
  mergeTagIds?: string[]
}

type Props = {
  fieldName: string
  isDisabled?: boolean
  isGenerating: boolean
  generatedValue?: string
  rawContent?: string
  rawContentType?: 'html' | 'text' | 'image'
  showPasteHtml: boolean
  showUploadPdf: boolean
  showUploadImage: boolean
  showUploadEml: boolean
  onGenerateBriefFromContent: (
    content: string,
    contentType: 'html' | 'text' | 'image',
    options?: GenerateBriefFromContentOptions
  ) => void
  onRemoveRawContent: () => void
}

const BriefContentImportActions = ({
  fieldName,
  isDisabled,
  isGenerating,
  generatedValue,
  rawContent,
  rawContentType,
  showPasteHtml,
  showUploadPdf,
  showUploadImage,
  showUploadEml,
  onGenerateBriefFromContent,
  onRemoveRawContent,
}: Props) => {
  const form = useForm()
  const hasRawSource = !!rawContent

  const [openModalTab, setOpenModalTab] = useState<ImportTab | null>(null)
  const [isRemoveConfirmOpen, setIsRemoveConfirmOpen] = useState(false)
  const isAwaitingResultRef = useRef(false)
  const previousGeneratedValueRef = useRef(generatedValue)
  const preImportValueRef = useRef<string | undefined>(undefined)

  useEffect(() => {
    if (
      isAwaitingResultRef.current &&
      generatedValue &&
      generatedValue !== previousGeneratedValueRef.current
    ) {
      form.initialize((values) => ({
        ...values,
        [fieldName]: generatedValue,
      }))
      isAwaitingResultRef.current = false
    }
    previousGeneratedValueRef.current = generatedValue
  }, [generatedValue, fieldName, form])

  const handleImportContent = (
    content: string,
    contentType: 'html' | 'text' | 'image'
  ) => {
    // Only capture the pre-import baseline on the *first* import — if the
    // user replaces an already-imported source without removing it first,
    // removal should still revert to the original value, not the
    // intermediate one from the previous import.
    if (!hasRawSource) {
      preImportValueRef.current = generatedValue
    }
    isAwaitingResultRef.current = true
    setOpenModalTab(null)

    const { values } = form.getState()
    onGenerateBriefFromContent(content, contentType, {
      brandVoiceId: (values.brand_voice as { value?: string } | undefined)
        ?.value,
      mergeTagIds: values.merge_tags as string[] | undefined,
    })
  }

  const handleRemoveRawContent = () => {
    // form.initialize (not form.change) so this reverts to a fresh pristine
    // baseline instead of marking the field dirty — a dirty field here would
    // flip BriefPage's state machine into the old "formChangedWithBrief"
    // state, which predates (and hides) the import-flow's Focus Themes /
    // Personalisation Tags bypass.
    form.initialize((values) => ({
      ...values,
      [fieldName]: preImportValueRef.current ?? '',
    }))
    onRemoveRawContent()
    setIsRemoveConfirmOpen(false)
  }

  const chipLabel =
    rawContentType === 'text'
      ? 'Imported PDF'
      : rawContentType === 'image'
        ? 'Imported Image'
        : 'Pasted HTML'
  const ChipIcon =
    rawContentType === 'text'
      ? FileIcon
      : rawContentType === 'image'
        ? ImageIcon
        : Clipboard

  return (
    <div className="pt-2">
      <div className="flex items-center gap-2">
        <MenuButton
          // This button lives inside the description textarea's own
          // scrollable/bordered box (it's rendered as that field's footer) —
          // without portal, the menu renders inline there too and ends up
          // visually clipped/overlapping the textarea instead of floating
          // above it.
          portal
          menuClassName={cx(
            // @szhsin/react-menu ships its own `.szh-menu { border: none;
            // background-color: #fff }` — same specificity as these
            // Tailwind classes, so depending on CSS bundling order it can
            // win. Force these with `!important` so they always apply.
            'shadow-2xl !border !border-solid !border-gold-400 !bg-gold-40',
            menuButtonStyles.menu
          )}
          offsetY={8}
          buttonProps={{
            disabled: isDisabled || isGenerating,
            'data-testid': 'brief-content-import-open-menu-button',
            className:
              'flex items-center justify-center px-4 py-2 rounded-full border border-gold-400 hover:border-base-700 disabled:opacity-50 disabled:cursor-not-allowed',
            children: <Add size={4} isDefaultColor={false} />,
          }}
          options={
            <>
              {showPasteHtml && (
                <MenuItem
                  hoverClassName="hover:bg-gold-300"
                  data-testid="paste-html-open-modal-button"
                  onClick={() => setOpenModalTab('html')}
                >
                  <Clipboard
                    isDefaultColor={false}
                    className="text-coolGray-400 mr-4"
                    size={4}
                  />
                  Paste HTML
                </MenuItem>
              )}
              {showUploadPdf && (
                <MenuItem
                  hoverClassName="hover:bg-gold-300"
                  data-testid="upload-pdf-open-modal-button"
                  onClick={() => setOpenModalTab('pdf')}
                >
                  <FileIcon
                    isDefaultColor={false}
                    className="text-coolGray-400 mr-4"
                    size={4}
                  />
                  Upload PDF
                </MenuItem>
              )}
              {showUploadImage && (
                <MenuItem
                  hoverClassName="hover:bg-gold-300"
                  data-testid="upload-image-open-modal-button"
                  onClick={() => setOpenModalTab('image')}
                >
                  <ImageIcon
                    isDefaultColor={false}
                    className="text-coolGray-400 mr-4"
                    size={4}
                  />
                  Upload Image
                </MenuItem>
              )}
              {showUploadEml && (
                <MenuItem
                  hoverClassName="hover:bg-gold-300"
                  data-testid="upload-eml-open-modal-button"
                  onClick={() => setOpenModalTab('eml')}
                >
                  <Email
                    isDefaultColor={false}
                    className="text-coolGray-400 mr-4"
                    size={4}
                  />
                  Upload Email
                </MenuItem>
              )}
            </>
          }
        />
        {hasRawSource && (
          <>
            <div className="w-px h-4 bg-gold-400" />
            <span
              className="flex items-center gap-1 px-3 py-1 text-xs font-medium rounded-full bg-maroon-40 text-maroon-500 border border-maroon-400"
              data-testid="raw-content-chip"
            >
              <ChipIcon size={4} isDefaultColor={false} />
              {chipLabel}
              <button
                type="button"
                onClick={() => setIsRemoveConfirmOpen(true)}
                disabled={isDisabled || isGenerating}
                data-testid="raw-content-remove-button"
              >
                <CloseSmall size={3} isDefaultColor={false} />
              </button>
            </span>
          </>
        )}
      </div>

      {isGenerating && (
        <p className="text-xs text-base-500 pt-1">
          Jacquard is summarising. May take a moment...
        </p>
      )}

      <ImportContentModal
        isOpen={openModalTab !== null}
        initialTab={openModalTab ?? 'html'}
        currentDescription={generatedValue}
        onClose={() => setOpenModalTab(null)}
        onImportHtml={(html) => handleImportContent(html, 'html')}
        onImportPdf={(text) => handleImportContent(text, 'text')}
        onImportImage={(dataUri) => handleImportContent(dataUri, 'image')}
        // The .eml tab extracts the email's HTML body client-side — from
        // content-api's point of view this is indistinguishable from a
        // pasted-HTML import, so it reuses the same 'html' content_type
        // rather than needing any new backend content_type value.
        onImportEml={(html) => handleImportContent(html, 'html')}
      />

      <ConfirmationModal
        open={isRemoveConfirmOpen}
        title="Remove imported content"
        confirmationText="Removing this will reset the brief description to what it was before you imported it. Please note this change before proceeding."
        confirmButtonText="Proceed"
        cancelButtonText="Cancel"
        confirmButtonVariant="primary"
        onCancel={() => setIsRemoveConfirmOpen(false)}
        onConfirm={handleRemoveRawContent}
      />
    </div>
  )
}

export default BriefContentImportActions
